import { Component, OnInit } from '@angular/core';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { SwUpdate, VersionEvent } from '@angular/service-worker';
import { Platform } from '@angular/cdk/platform';
import { Observable } from 'rxjs';
import { Clipboard } from '@angular/cdk/clipboard';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { ClipboardModule } from '@angular/cdk/clipboard';
import { MatDividerModule } from '@angular/material/divider';
import { MatCardModule } from '@angular/material/card';
@Component({
  selector: 'app-update-dialog',
  templateUrl: './update-dialog.component.html',
  styleUrls: ['./update-dialog.component.scss'],
  providers: [Platform],
  imports: [
    MatDialogModule,
    MatIconModule,
    MatButtonModule,
    MatSnackBarModule,
    ClipboardModule,
    MatDividerModule,
    MatCardModule
  ],
  standalone: true
})
export class UpdateDialogComponent implements OnInit {
  isUpdateAvailable = false;
  isCheckingForUpdate = false;
  isEnabled = this.swUpdate.isEnabled;
  versionUpdates: Observable<VersionEvent>;
  updateError: string | null = null;

  // Platform information
  platformInfo = {
    isBrowser: this.platform.isBrowser,
    isAndroid: this.platform.ANDROID,
    isIOS: this.platform.IOS,
    isWebOS: this.platform.WEBKIT,
    isFirefox: this.platform.FIREFOX,
    isSafari: this.platform.SAFARI,
    isChrome: this.platform.BLINK,
    isTrident: this.platform.TRIDENT,
    isEdge: this.platform.EDGE
  };

  // Version information
  versionInfo: any = {
    serviceWorkerEnabled: this.swUpdate.isEnabled,
    appVersion: 'Unknown', // This should be populated from your environment
    currentHash: 'Unknown' // Will be updated when available
  };

  constructor(
    public dialogRef: MatDialogRef<UpdateDialogComponent>,
    private swUpdate: SwUpdate,
    private platform: Platform,
    private clipboard: Clipboard,
    private snackBar: MatSnackBar
  ) {
    this.versionUpdates = this.swUpdate.versionUpdates;
  }

  ngOnInit(): void {
    // Subscribe to version updates
    this.swUpdate.versionUpdates.subscribe((event: VersionEvent) => {
      switch (event.type) {
        case 'NO_NEW_VERSION_DETECTED':
          this.isUpdateAvailable = false;
          if (event.version && event.version.hash) {
            this.versionInfo.currentHash = event.version.hash;
          }
          break;
        case 'VERSION_DETECTED':
          this.isUpdateAvailable = true;
          if (event.version && event.version.hash) {
            this.versionInfo.newHash = event.version.hash;
          }
          break;
        case 'VERSION_READY':
          this.isUpdateAvailable = true;
          if (event.currentVersion && event.currentVersion.hash) {
            this.versionInfo.currentHash = event.currentVersion.hash;
          }
          if (event.latestVersion && event.latestVersion.hash) {
            this.versionInfo.newHash = event.latestVersion.hash;
          }
          break;
        case 'VERSION_INSTALLATION_FAILED':
          this.updateError = `Installation failed: ${event.error}`;
          break;
      }
    });

    // Get current version hash if available
    this.swUpdate
      .checkForUpdate()
      .then(() => {
        // This will trigger the versionUpdates observable if an update is available
      })
      .catch((err) => {
        this.updateError = `Error checking for updates: ${err.message}`;
      });
  }

  checkForUpdate(): void {
    this.isCheckingForUpdate = true;
    this.updateError = null;

    this.swUpdate
      .checkForUpdate()
      .then((hasUpdate) => {
        this.isCheckingForUpdate = false;
        if (!hasUpdate && !this.isUpdateAvailable) {
          this.snackBar.open('No updates available', 'Close', {
            duration: 3000
          });
        }
      })
      .catch((err) => {
        this.isCheckingForUpdate = false;
        this.updateError = `Error checking for updates: ${err.message}`;
        this.snackBar.open('Error checking for updates', 'Close', {
          duration: 3000
        });
      });
  }

  activateUpdate(): void {
    this.swUpdate
      .activateUpdate()
      .then(() => {
        this.snackBar.open('Update activated. Reloading page...', 'Close', {
          duration: 2000
        });
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      })
      .catch((err) => {
        this.updateError = `Error activating update: ${err.message}`;
        this.snackBar.open('Error activating update', 'Close', {
          duration: 3000
        });
      });
  }

  copyInfo(): void {
    const info = {
      version: this.versionInfo,
      platform: this.platformInfo,
      timestamp: new Date().toISOString(),
      url: window.location.href
    };

    this.clipboard.copy(JSON.stringify(info, null, 2));
    this.snackBar.open('Information copied to clipboard', 'Close', {
      duration: 3000
    });
  }

  close(): void {
    this.dialogRef.close();
  }
}
